import React, { useContext, useEffect, useRef, useState } from 'react'
import styles from './EfoPostalCode.module.css'
import { FormTitle } from '../../../../Common/FormTitle'
import { NextButton } from '../../../../Common/CtaButton/NextButton'
import { FormField } from '../../../../Common/FormField'
import { MenuArrow } from '../../../../Common/Pulldown/MenuArrow'
import { validateZipCode } from '../../Prefecture/validate'
import { useAbTestGtm } from '../../../../../hooks/useAbTestGtm'
import { StepContext, StepIndexContext } from '../../../StepContainer'
import { fetchCities } from '../../../../../app/api/cityApi'
import type { City } from '../../../../../app/api/cityApi/types'
import { fetchPrefectureOfferCounts } from '../../../../../app/api/offerCountsApi'
import type { PrefectureOfferCounts } from '../../../../../app/api/offerCountsApi/types'
import { PREFECTURE_OPTION_GROUPS } from './prefectureOptions'
import { ZIPCODE_LENGTH } from '../../../../../constants/progressBanner'
import { EfoAddressSelects } from './EfoAddressSelects'
import {
    changeZip,
    failZip,
    initialEfoAddressState,
    isAddressComplete,
    isZipDisabled,
    resolveOfferCount,
    resolveZip,
    selectCity,
    selectPrefecture,
} from './efoAddressState'

const DUMMY_OFFER_COUNT = '1,000'
const ZIP_NOT_FOUND_ERROR = '該当する住所が見つかりませんでした。'
const CITIES_FETCH_ERROR =
    '市区町村を取得できませんでした。もう一度都道府県を選ぶか、郵便番号を入力してください。'

export const EfoPostalCode = () => {
    const { dataClickLabel, dataViewLabel } = useAbTestGtm()
    const { currentStep } = useContext(StepContext)
    const stepIndex = useContext(StepIndexContext)

    const [address, setAddress] = useState(initialEfoAddressState)
    const [cities, setCities] = useState<City[]>([])
    const [citiesError, setCitiesError] = useState('')
    const [isSelectOpen, setIsSelectOpen] = useState(false)
    // null = 未取得。取得できるまでは件数を出さずモザイク表示を続ける
    const [offerCounts, setOfferCounts] = useState<PrefectureOfferCounts | null>(null)
    const [displayAddress, setDisplayAddress] = useState('')
    const [zipError, setZipError] = useState('')

    // 全ステップが最初からマウントされるため、マウント時ではなくこのステップが表示された
    // タイミングで1回だけ取得する（戻る・進むで取得し直さない）
    const isCurrentStep = currentStep === stepIndex
    const hasRequestedOfferCounts = useRef(false)
    useEffect(() => {
        if (!isCurrentStep || hasRequestedOfferCounts.current) return

        hasRequestedOfferCounts.current = true
        fetchPrefectureOfferCounts()
            .then(setOfferCounts)
            // 取得できなくてもフォームは動く（件数はモザイク表示のまま）
            .catch(() => {})
    }, [isCurrentStep])

    useEffect(() => {
        if (address.zip) setCitiesError('')
        if (address.zip.length !== ZIPCODE_LENGTH.MAX) {
            setDisplayAddress('')
            setZipError('')
            return
        }

        let isStale = false
        const fail = (error: string) => {
            setAddress(failZip)
            setDisplayAddress('')
            setZipError(error)
        }
        validateZipCode(address.zip)
            .then(result => {
                if (isStale) return

                if (result.isValid && result.data) {
                    setAddress(current => resolveZip(current, result.data))
                    setDisplayAddress(result.data.address)
                    setZipError('')
                } else {
                    fail(result.error)
                }
            })
            // 通信エラー（fetchZipCode が throw する）でも理由が分かるようにする
            .catch(() => {
                if (!isStale) fail(ZIP_NOT_FOUND_ERROR)
            })

        return () => {
            isStale = true
        }
    }, [address.zip])

    useEffect(() => {
        const { prefectureId, isZipResolved } = address
        if (prefectureId === null) {
            setCities([])
            return
        }

        // 前の都道府県の市区町村を選べないよう、新しい一覧が届くまで空にしておく
        setCities([])
        setCitiesError('')
        let isStale = false
        fetchCities({ prefectureId }).then(response => {
            if (isStale) return

            if (response.success && response.data) {
                setCities(response.data)
            } else if (!isZipResolved) {
                // 郵便番号で確定した住所は一覧が無くても登録できるので残す。手動選択中だけ、
                // 都道府県の選択を外して郵便番号を入力できるようにし、選び直せば再取得されるようにする
                setCitiesError(CITIES_FETCH_ERROR)
                setAddress(current =>
                    current.prefectureId === prefectureId && !current.isZipResolved
                        ? selectPrefecture(current, null)
                        : current
                )
            }
        })

        return () => {
            isStale = true
        }
    }, [address.prefectureId])

    const offerCount = resolveOfferCount(offerCounts, address.prefectureId)
    const cityName = cities.find(city => city.id === address.cityId)?.name ?? ''
    const prefectureName =
        PREFECTURE_OPTION_GROUPS.flatMap(group => group.prefectures).find(
            prefecture => prefecture.id === address.prefectureId
        )?.name ?? ''

    return (
        <div className={styles.wrapper}>
            <div className={styles.selectionWrapper}>
                <div className={styles.monthbox}>
                    <FormTitle
                        formTitleText="お住まいの地域はどこですか？"
                        dataViewLabel={dataViewLabel('EF_StepForm_住まい')}
                        dataClickLabel={dataClickLabel('EF_StepForm_住まい-戻る')}
                    />
                </div>

                <div className={styles.field}>
                    <span className={styles.zipBadge}>
                        ハイフンなし あと{ZIPCODE_LENGTH.MAX - address.zip.length}桁
                    </span>
                    <FormField
                        label="郵便番号"
                        placeholder="1234567"
                        value={address.zip}
                        isInputType="tel"
                        onChange={value =>
                            setAddress(current => changeZip(current, value))
                        }
                        maxLength={ZIPCODE_LENGTH.MAX}
                        disabled={isZipDisabled(address)}
                    />
                    {zipError && <p className={styles.errorMessages}>{zipError}</p>}
                </div>

                <p className={styles.offerCountPill}>
                    ご希望エリア付近の求人数
                    {offerCount === null ? (
                        <span className={styles.offerCountMasked} aria-hidden="true">
                            {DUMMY_OFFER_COUNT}
                        </span>
                    ) : (
                        <span>{offerCount.toLocaleString()}</span>
                    )}
                    件
                </p>

                <button
                    type="button"
                    className={styles.selectToggle}
                    onClick={() => setIsSelectOpen(current => !current)}
                    data-click-label={dataClickLabel(
                        'EF_StepForm_住まい-郵便番号がわからない方'
                    )}
                >
                    郵便番号がわからない方はこちら
                    <MenuArrow isOpen={isSelectOpen} />
                </button>

                {isSelectOpen && (
                    <EfoAddressSelects
                        prefectureId={address.prefectureId}
                        cityId={address.cityId}
                        cities={cities}
                        citiesError={citiesError}
                        isDisabled={address.isZipResolved}
                        onPrefectureChange={prefectureId =>
                            setAddress(current =>
                                selectPrefecture(current, prefectureId)
                            )
                        }
                        onCityChange={cityId =>
                            setAddress(current => selectCity(current, cityId))
                        }
                    />
                )}

                <NextButton
                    formKey={[
                        'prefecture',
                        'city',
                        'postalCode',
                        'cityAddressId',
                    ]}
                    formValue={[
                        address.prefectureId,
                        address.cityId,
                        address.zip,
                        address.cityAddressId,
                    ]}
                    displayFormKey={'prefectureCity'}
                    displayFormValue={
                        displayAddress || `${prefectureName}${cityName}`
                    }
                    isDisabled={!isAddressComplete(address)}
                    dataClickLabel={dataClickLabel('EF_StepForm_住まい-次へ')}
                />
            </div>
        </div>
    )
}
